// A budget refusal, and the one place that knows it is not an outage.
//
// ── WHY THIS FILE EXISTS ─────────────────────────────────────────────────────
//
// `server/gate.mjs` rations this app's calls to the operators, and when it says
// no the route in `server/ptv.mjs` answers 503 with `error: "busy"`, a
// `Retry-After` and `configured: true`. Every word of that is deliberate: the
// credentials are fine, the operator is up, and the only thing that happened is
// that this app asked for more in one minute than it allows itself.
//
// The client threw all of that away. A 503 fell through the same `if (!ok)`
// as a timeout and a 502, the screen said "Couldn't reach PTV. Pull down to try
// again", and both halves were wrong: it blamed the operator for our own
// rationing, and it asked for the one action that cannot help, because pulling
// down immediately spends the budget that is already spent. Somebody planning
// Carnegie to Richmond was told the transport network was unreachable by an app
// that had spent its own minute decorating a board.
//
// So a busy answer is now its own kind of answer:
//
//   * IT RETRIES ITSELF ONCE, silently, after the number of seconds the server
//     named. That is where the refusal usually ends, because the gate refills
//     continuously and a second attempt five seconds later finds a token.
//   * A MESSAGE ONLY AFTER THAT SECOND ATTEMPT ALSO FAILS, and the message says
//     what is true: this app is limiting itself, the operator is fine, a few
//     seconds will fix it.
//   * A DEAD UPSTREAM STILL SAYS SO. Nothing here touches a 502, a timeout or
//     an unconfigured server. Only a 503 that names itself `busy` is treated as
//     busy, so an outage cannot hide behind a spinner.

import { city } from './city.js';

/**
 * The gate said no, and said so in as many words.
 *
 * An `Error`, because every screen in this app already handles a thrown
 * failure and a second channel would be a second thing to forget. The `busy`
 * flag is what tells them apart, and `isBusy` is the only correct way to read
 * it — a message match would break the first time the wording changed.
 */
export class BusyError extends Error {
  constructor(message, retryAfterSeconds = 5) {
    super(message);
    this.name = 'BusyError';
    this.busy = true;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/** Whether a failure is the budget rationing us, rather than anything breaking. */
export const isBusy = (e) => Boolean(e?.busy);

/**
 * How long to wait before the one retry, in milliseconds.
 *
 * The server's figure, clamped. Zero or nonsense becomes the five seconds the
 * route actually sends, and anything absurd is capped: a retry nobody is still
 * on the screen for is a retry that costs a call and buys nothing.
 */
const MIN_WAIT_MS = 1_000;
const MAX_WAIT_MS = 8_000;

export function retryDelayMs(retryAfterSeconds) {
  const s = Number(retryAfterSeconds);
  if (!Number.isFinite(s) || s <= 0) return 5_000;
  return Math.min(MAX_WAIT_MS, Math.max(MIN_WAIT_MS, Math.round(s * 1_000)));
}

/**
 * Whether this answer is the gate saying no, and for how long.
 *
 * BOTH HALVES ARE REQUIRED. A bare 503 is any server having a bad day; only a
 * 503 whose body names itself `busy` is this app's own budget, and only that
 * one may be retried silently and described as temporary.
 */
export function busyAnswer(status, body) {
  if (Number(status) !== 503 || body?.error !== 'busy') return null;
  return { retryAfterSeconds: Number(body.retryAfterSeconds) || 5 };
}

/** A pause, for the one retry. */
export const pause = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

/**
 * One attempt, and one retry after the delay the server named.
 *
 * `attempt` answers `{ ok, body, status }` and never throws — every `ask` in
 * this app already swallows its own network failure into that shape — so the
 * retry here is about the budget alone and not a second chance at a dead
 * network.
 */
export async function askPatiently(attempt) {
  const first = await attempt();
  const busy = busyAnswer(first.status, first.body);
  if (!busy) return first;

  await pause(retryDelayMs(busy.retryAfterSeconds));
  const second = await attempt();
  const again = busyAnswer(second.status, second.body);
  // The retry's own answer wins, whatever it is: a second refusal is a busy
  // answer, and a 502 on the way back is an outage that started in between.
  return again ? { ...second, busy: again } : second;
}

/**
 * What a screen says when the budget refused twice.
 *
 * ONE VOICE AND ONE FACT: the operator is fine, this app is the thing holding
 * back, and the wait is seconds rather than an outage. It names the source the
 * way every other sentence in the app does, so "PTV" in Melbourne and "TfNSW"
 * in Sydney.
 */
export const BUSY_HEAD = 'Busy for a moment';

export const busyCopy = () => `This app limits how often it asks ${city().sourceShort}, and it just reached that limit. `
  + `${city().sourceShort} is fine. Give it a few seconds.`;

export const busyLine = () => `Busy for a moment, not ${city().sourceShort}. Give it a few seconds.`;

/** The failure a screen should throw for an answer that came back busy twice. */
export const busyError = (answer) => new BusyError(busyLine(), answer?.busy?.retryAfterSeconds ?? 5);

// ── AND THE OTHER KIND OF SHORT ANSWER: ONE THAT TOOK TOO LONG ───────────────
//
// A busy answer is the gate refusing. This is nobody refusing anything: the
// calls are going out, they are coming back, and there are simply more of them
// than fit in the time anybody will watch a loader. The trip planner stops at
// its own deadline and hands back what it built, which is sometimes three
// options, sometimes one, and sometimes none at all.
//
// It gets its own words because the two true sentences are different. Busy
// says "we asked for too much this minute and it is over in seconds". Slow
// says "this took longer than it should and here is what we have". Neither of
// them is "couldn't reach PTV", and none of the three may be shown for
// another's failure.

export const SLOW_HEAD = 'That took too long';

export const slowCopy = () => `${city().sourceShort} is answering slowly and Late Again stopped waiting rather than leave you on a spinner. `
  + 'Pull down to try again. The second attempt is usually quick, because the answers this one paid for are cached.';

export const SLOW_LINE = 'These are the trains found before the plan ran out of time.';

/**
 * The heading and the copy for a failure, whichever kind it is.
 *
 * The screens each had their own hard-coded "Couldn't reach PTV" pair, which is
 * how a distinction the server draws carefully gets lost on the way to the
 * glass. They read this instead, and a busy failure reads as busy everywhere at
 * once.
 */
export function failureText(error, { head, copy }) {
  if (!isBusy(error)) return { head, copy, busy: false };
  return { head: BUSY_HEAD, copy: busyCopy(), busy: true };
}
